import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Switch } from '@mui/material';
import { fetchClientOverview, setTableSort, setTabPage, setClientType, setTotalGoalToggle, refreshAccountOverview } from '../../redux/accountOverviewSlice';
import { selectEntityType } from '../../redux/authSlice';
import { ApiCall } from '../../helper/axios';
import InfoTooltip from '../../common_components/InfoTooltip';
import { setActiveModuleKey, clearPermissionDenied } from '../../redux/userDataSlice';
// Common Components
import PageContainer from '../../common_components/PageContainer';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import { Dropdown, ExportButton } from '../../common_components/FormInputs';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import CommonLoader from '../../common_components/CommonLoader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import CustomDatePicker from '../../common_components/daterangepicker/CustomDatePicker';
import CommonTabs from '../../common_components/CommonTabs';
import PublisherLimitModal from './components/PublisherLimitModal';
import AccountOverviewPDFContent from './components/AccountOverviewPDFContent';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
// Hooks
import { useClientSelection, useDateRange, usePageAccessControl } from '../../hooks';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import { useNavigate, useLocation } from 'react-router-dom';
// Components
import MetricsGrid from './components/MetricsGrid';
import PublisherTable from './components/PublisherTable';
import PublisherTracker from '../PublisherTracker/PublisherTracker';
import SocialYtTrackerList from '../SocialYtTracker/SocialYtTrackerList';
import SampleTrackerList from '../SampleTracker/SampleTrackerList';
// Utils
import { NETWORK_METADATA_MAP, PAID_MEDIA_NETWORKS } from '../../utils/networkConstants';
import { truncateName } from '../../helper/truncateName';
const PARENT_MODULE_KEY = 'affiliate_account_performance';
const SECTION_TABS = [
    { label: 'Account Overview', value: 'account_overview' },
    { label: 'Publisher Tracker', value: 'publisher_tracker' },
    { label: 'Social Tracker', value: 'social_tracker' },
    { label: 'Youtube Tracker', value: 'youtube_tracker' },
    { label: 'Sample Tracker', value: 'sample_tracker' },
];
const AccountOverview = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedClientId: contextClientId, selectedMonth, dateRange: contextDateRange, // ← ADD
    compareDateRange: contextCompareRange, // ← ADD
    isCompareOn: contextIsCompareOn, // ← ADD
    setClientId, setDateRange: setContextDateRange, setCompareDateRange: setContextCompareRange, setIsCompareOn: setContextCompareOn, clientType: contextClientType, totalGoalToggle: contextTotalGoalToggle, setClientType: setContextClientType, setTotalGoalToggle: setContextGoalToggle, isArchiveMode, setIsArchiveMode, activeTab, setActiveTab } = useAccountOverview();
    // ✅ Set module key, reset activeTab to 'account_overview', and reset isArchiveMode to false (Active) on mount
    useEffect(() => {
        dispatch(setActiveModuleKey(PARENT_MODULE_KEY));
        const state = location.state;
        if (state?.restoreTab) {
            setActiveTab(state.restoreTab);
        }
        else {
            setActiveTab('account_overview');
        }
        setIsArchiveMode(false);
    }, [dispatch, setActiveTab, setIsArchiveMode, location.state]);
    // ✅ Sync Context to Redux for clientType and totalGoalToggle
    useEffect(() => {
        if (contextClientType && contextClientType !== clientType) {
            dispatch(setClientType(contextClientType));
        }
        if (contextTotalGoalToggle !== totalGoalToggle) {
            dispatch(setTotalGoalToggle(contextTotalGoalToggle));
        }
    }, [contextClientType, contextTotalGoalToggle, dispatch]);
    // Clear permission-denied state when switching tabs
    // Keep the same module key (affiliate_account_performance) for all tabs within Account Overview
    useEffect(() => {
        dispatch(clearPermissionDenied());
    }, [dispatch, activeTab]);
    const { selectedClientId, selectedGroupAccountId, selectedId, selectedClient, clientList, clientOptions, groupAccountOptions, currentSelectedGroupAccountLabel, utilityLoading, handleClientChange, handleGroupChange, } = useClientSelection('affiliate_account_performance', {
        ignoreCache: true,
        status: isArchiveMode ? 'inactive' : 'active'
    });
    const { dateRange, compareDateRange, isCompareOn, handleDateChange } = useDateRange({ defaultComparison: true, defaultCompareType: 'mom' });
    // ✅ Centralized Access & Redirection Control
    usePageAccessControl(selectedId);
    const handleClientChangeWithContext = useCallback((val) => {
        const id = Array.isArray(val) ? val[0] : val;
        setClientId(id);
        handleClientChange(val);
    }, [handleClientChange, setClientId]);
    const handleDateChangeWithContext = useCallback((data) => {
        handleDateChange(data);
        // Update context with string values for persistence
        setContextDateRange({
            start: data.mainRange?.start?.toString() || null,
            end: data.mainRange?.end?.toString() || null,
        });
        if (data.compareRange) {
            setContextCompareRange({
                start: data.compareRange?.start?.toString() || null,
                end: data.compareRange?.end?.toString() || null,
            });
            setContextCompareOn(true);
        }
        else {
            setContextCompareRange(null);
            setContextCompareOn(false);
        }
    }, [handleDateChange, setContextDateRange, setContextCompareRange, setContextCompareOn]);
    const { connections, allConnections, summary, tabsWithRows, tableState, lastUpdated, overallStatus, loading, refreshLoading, clientType, totalGoalToggle, clientName, affiliateAccountType, affiliate_measurement_channel } = useSelector((state) => state.accountOverview);
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = useMemo(() => {
        return permissions?.affiliate_account_performance?.actions || permissions?.affiliateAccountPerformance?.actions || [];
    }, [permissions]);
    const hasAffiliateNetwork = useMemo(() => {
        if (!selectedClient && !selectedId)
            return false;
        const paidMediaNames = PAID_MEDIA_NETWORKS.map(n => n.name.toLowerCase());
        // 1. Check connected_id on selectedClient
        const clientConnectionsList = selectedClient?.connected_id || [];
        const hasConnectedAffiliate = clientConnectionsList.some((c) => {
            if (c.status?.toLowerCase() === 'inactive' || c.api_enabled === false)
                return false;
            const net = (c.network || '').toLowerCase();
            return !paidMediaNames.includes(net);
        });
        if (hasConnectedAffiliate)
            return true;
        // 2. Check connected_networks on selectedClient
        const clientNetworksObj = selectedClient?.connected_networks || {};
        const hasAffiliateInObj = Object.keys(clientNetworksObj).some(net => {
            if (!clientNetworksObj[net])
                return false;
            return !paidMediaNames.includes(net.toLowerCase());
        });
        if (hasAffiliateInObj)
            return true;
        // 3. Check allConnections from Redux
        if (allConnections && allConnections.length > 0) {
            const hasInAllConnections = allConnections.some(c => {
                if (c.status?.toLowerCase() === 'inactive' || c.api_enabled === false)
                    return false;
                const net = (c.network || '').toLowerCase();
                return !paidMediaNames.includes(net);
            });
            if (hasInAllConnections)
                return true;
        }
        return false;
    }, [selectedClient, selectedId, allConnections]);
    const visibleTabs = useMemo(() => {
        const hasSampleTracker = affiliateActions.includes('Sample Tracker');
        return SECTION_TABS.filter(tab => {
            if (tab.value === 'account_overview')
                return hasAffiliateNetwork;
            if (tab.value === 'sample_tracker')
                return hasSampleTracker;
            return true;
        });
    }, [affiliateActions, hasAffiliateNetwork]);
    // If client has no connected affiliate network and current tab is account_overview, switch to publisher_tracker
    // Only switch when client data is fully loaded and confirmed to not have an affiliate network
    useEffect(() => {
        if (!utilityLoading && selectedClient && !hasAffiliateNetwork && activeTab === 'account_overview') {
            setActiveTab('publisher_tracker');
        }
    }, [utilityLoading, selectedClient, hasAffiliateNetwork, activeTab, setActiveTab]);
    const STATUS_MAP = {
        completed: "Completed", not_found: "Completed",
        pending: "In process", in_progress: "In process", failed: "Failed",
    };
    const statusText = STATUS_MAP[overallStatus || ""] || "Completed";
    const [selectedConnectionId, setSelectedConnectionId] = useState('all');
    // CJ-only "Action" filter (All / Retail / Wholesale). Shown when a CJ connection is in view (All Networks,
    // or the chosen network). Until the user picks one, it sits on the connection's global Action Setting
    // Value (several connections that disagree -> All) and nothing is sent, so the connection's own setting applies.
    const [selectedAction, setSelectedAction] = useState(null);
    const cjConnectionsInView = useMemo(() => allConnections.filter(c => c.network?.toLowerCase() === 'cj'
        && c.status?.toLowerCase() !== 'inactive'
        && (selectedConnectionId === 'all' || c.connectionId === selectedConnectionId)), [allConnections, selectedConnectionId]);
    const showActionDropdown = cjConnectionsInView.length > 0;
    const defaultAction = useMemo(() => {
        const values = cjConnectionsInView.map(c => c.action_setting_value || 'all');
        return values.length > 0 && values.every(v => v === values[0]) ? values[0] : 'all';
    }, [cjConnectionsInView]);
    const effectiveAction = selectedAction ?? defaultAction;
    // Only an explicit pick is sent ('all' there = every tracker, ignoring the connection's global setting).
    const actionSettingParam = showActionDropdown && selectedAction !== null ? selectedAction : undefined;
    const [activeFunnelTab, setActiveFunnelTab] = useState('all');
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    // 'all_publishers' merges the hidden buckets (spam/pending/0-click), so clients don't get it either.
    const CLIENT_HIDDEN_TABS = ['spam', 'zero_clicks', 'pending', 'all_publishers'];
    const visibleTabsWithRows = useMemo(() => {
        if (!isClient)
            return tabsWithRows;
        return tabsWithRows.filter(t => {
            const key = t.key?.toLowerCase();
            const label = t.label?.toLowerCase();
            return !CLIENT_HIDDEN_TABS.includes(key) && !['spam', '0 click', 'pending'].includes(label);
        });
    }, [tabsWithRows, isClient]);
    // Only reset funnel tab if the current key is no longer in the visible tabs (e.g. after client switch)
    useEffect(() => {
        if (visibleTabsWithRows.length === 0)
            return;
        const exists = visibleTabsWithRows.some(t => t.key === activeFunnelTab);
        if (!exists) {
            const allTab = visibleTabsWithRows.find(t => t.key === 'all');
            setActiveFunnelTab(allTab ? 'all' : visibleTabsWithRows[0].key);
        }
    }, [visibleTabsWithRows, activeFunnelTab]);
    // ── Core fetch — called whenever client, dates OR network changes ──────────
    const fetchData = useCallback(() => {
        if (!selectedId || !dateRange?.start || !dateRange?.end) {
            return;
        }
        dispatch(fetchClientOverview({
            clientId: selectedId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString(),
            ...(isCompareOn && compareDateRange?.start && compareDateRange?.end && {
                compareStartDate: compareDateRange.start.toString(),
                compareEndDate: compareDateRange.end.toString(),
            }),
            ...(selectedConnectionId !== 'all' && { connectionId: selectedConnectionId }),
            ...(actionSettingParam && { actionSetting: actionSettingParam }),
            // Table state forwarded to backend
            pages: tableState.pages,
            limit: tableState.limit,
            sortBy: tableState.sortBy,
            sortOrder: tableState.sortOrder,
            clientType,
        }));
    }, [
        selectedId,
        dateRange?.start?.toString(),
        dateRange?.end?.toString(),
        isCompareOn,
        compareDateRange?.start?.toString(),
        compareDateRange?.end?.toString(),
        selectedConnectionId,
        actionSettingParam,
        tableState.sortBy,
        tableState.sortOrder,
        tableState.limit,
        JSON.stringify(tableState.pages),
        clientType,
        dispatch
    ]);
    useEffect(() => {
        if (!utilityLoading && selectedId) {
            fetchData();
        }
    }, [fetchData, selectedId]);
    const [exporting, setExporting] = useState(false);
    const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
    const [publisherLimit, setPublisherLimit] = useState(10);
    const pdfRef = useRef(null);
    const handleExportOpen = useCallback(() => {
        setIsLimitModalOpen(true);
    }, []);
    // Rows fetched specifically for the PDF (the on-screen table only holds one page per tab).
    const [exportTabs, setExportTabs] = useState(null);
    const authToken = useSelector((state) => state.auth?.token);
    const handleExportSubmit = useCallback(async (limit) => {
        setPublisherLimit(limit);
        setIsLimitModalOpen(false);
        setExporting(true);
        try {
            // Fetch enough rows per tab: top-N overall is always within top-N of its own tab.
            const res = await ApiCall('POST', '/api/affiliate-account-overview/report', {
                clientId: selectedId,
                startDate: dateRange.start.toString(),
                endDate: dateRange.end.toString(),
                ...(isCompareOn && compareDateRange?.start && compareDateRange?.end && {
                    compareStartDate: compareDateRange.start.toString(),
                    compareEndDate: compareDateRange.end.toString(),
                }),
                ...(selectedConnectionId !== 'all' && { connectionId: selectedConnectionId }),
                ...(actionSettingParam && { actionSetting: actionSettingParam }),
                pages: {},
                limit: limit === 'ALL' ? 10000 : limit,
                sortBy: 'revenue',
                sortOrder: 'desc',
                clientType,
            }, { Authorization: `Bearer ${authToken}` });
            const fetched = res?.data?.success ? res.data.data?.tabs_with_rows : null;
            setExportTabs(fetched ?? null);
        }
        catch (err) {
            console.error('Export data fetch failed, using loaded rows:', err);
            setExportTabs(null);
        }
        // Give React a moment to update the hidden div with the new rows
        setTimeout(async () => {
            if (!pdfRef.current) {
                setExporting(false);
                return;
            }
            try {
                const dataUrl = await toPng(pdfRef.current, { quality: 0.95, backgroundColor: '#fff' });
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgProps = pdf.getImageProperties(dataUrl);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
                // Slice the tall image across as many A4 pages as needed (otherwise rows past
                // the first page are clipped).
                let offset = 0;
                pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, imgHeight);
                while (imgHeight - (offset + pageHeight) > 0) {
                    offset += pageHeight;
                    pdf.addPage();
                    pdf.addImage(dataUrl, 'PNG', 0, -offset, pdfWidth, imgHeight);
                }
                pdf.save(`${(clientName || 'Client').replace(/\s+/g, '_')}_Account_Overview.pdf`);
            }
            catch (err) {
                console.error('PDF generation failed:', err);
            }
            finally {
                setExporting(false);
                setExportTabs(null);
            }
        }, 800);
    }, [clientName, selectedId, dateRange, isCompareOn, compareDateRange, selectedConnectionId, actionSettingParam, clientType, authToken]);
    // Reset network filter, action filter & funnel tab when client changes
    useEffect(() => {
        setSelectedConnectionId('all');
        setSelectedAction(null);
        setActiveFunnelTab('all');
    }, [selectedId]);
    // ── Table sort/page handlers (dispatch → triggers re-fetch via tableState deps) ──
    const handleSort = useCallback((field, order) => {
        dispatch(setTableSort({ sortBy: field, sortOrder: order }));
    }, [dispatch]);
    const handlePageChange = useCallback((tabKey, page) => {
        dispatch(setTabPage({ tabKey, page }));
    }, [dispatch]);
    const handleFunnelTabChange = useCallback((key) => {
        setActiveFunnelTab(key);
    }, []);
    const handleRefresh = useCallback(async () => {
        if (!selectedId || !dateRange?.start || refreshLoading)
            return;
        await dispatch(refreshAccountOverview({
            clientId: selectedId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString()
        })).unwrap();
        fetchData();
    }, [dispatch, selectedId, dateRange, refreshLoading, fetchData]);
    // ── Memoised — icons & dropdown always use allConnections (full list) ─────
    const networkGroups = useMemo(() => {
        return allConnections.reduce((acc, c) => {
            const net = c.network?.toLowerCase();
            if (!acc[net])
                acc[net] = [];
            acc[net].push(c);
            return acc;
        }, {});
    }, [allConnections]);
    const uniqueNetworkIcons = useMemo(() => {
        const paidMediaNames = PAID_MEDIA_NETWORKS.map(n => n.name.toLowerCase());
        const groupedByIcon = {};
        // Process all affiliate connections (matches networkOptions)
        allConnections.forEach(c => {
            const netKey = c.network?.toLowerCase();
            if (!netKey)
                return;
            if (paidMediaNames.includes(netKey))
                return;
            if (netKey === 'shareasale')
                return; // Same filtering as outside page
            const meta = NETWORK_METADATA_MAP[netKey];
            if (!meta)
                return;
            const iconData = meta;
            const iconUrl = iconData.icon;
            if (!groupedByIcon[iconUrl]) {
                groupedByIcon[iconUrl] = {
                    icon: iconUrl,
                    name: iconData.name,
                    accounts: []
                };
            }
            groupedByIcon[iconUrl].accounts.push(c);
        });
        return Object.values(groupedByIcon);
    }, [allConnections]);
    const networkOptions = useMemo(() => {
        const paidMediaNames = PAID_MEDIA_NETWORKS.map(n => n.name.toLowerCase());
        const options = allConnections
            .filter(c => {
            const netKey = c.network?.toLowerCase();
            if (paidMediaNames.includes(netKey))
                return false;
            return true;
        })
            .map(c => {
            const netKey = c.network?.toLowerCase();
            const meta = NETWORK_METADATA_MAP[netKey];
            return {
                value: c.connectionId,
                label: c.accountName || c.network,
                networkKey: netKey,
                icon: meta?.icon || '/assets/link.svg'
            };
        });
        return [{ value: 'all', label: 'All Networks', networkKey: 'all' }, ...options];
    }, [allConnections]);
    const selectedOption = useMemo(() => (networkOptions.find(o => o.value === selectedConnectionId) || networkOptions[0]), [selectedConnectionId, networkOptions]);
    const cjActionOptions = useMemo(() => [
        { value: 'all', label: 'All' },
        { value: 'retail_purchase', label: 'Retail Purchase' },
        { value: 'wholesale_purchase', label: 'Wholesale Purchase' },
    ], []);
    const selectedActionLabel = cjActionOptions.find(o => o.value === effectiveAction)?.label || 'All';
    // ── Loading ───────────────────────────────────────────────────────────────
    if (utilityLoading) {
        return (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: 'center' }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    // ── Render ────────────────────────────────────────────────────────────────
    return (_jsx(PageContainer, { children: _jsx(Box, { sx: { position: 'relative', display: 'flex', flexDirection: 'column', gap: '8px' }, children: refreshLoading ? (_jsx(Box, { sx: {
                    // position: "absolute",
                    inset: 0,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Box, { sx: {
                                        minWidth: '200px',
                                        position: 'relative',
                                        '& .archive-mode-empty .dropdown-trigger': {
                                            borderColor: 'rgba(102, 112, 133, 0.3)',
                                            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                        },
                                        '@keyframes pulse': {
                                            '0%, 100%': {
                                                borderColor: 'rgba(102, 112, 133, 0.3)',
                                                boxShadow: '0 0 0 0 rgba(102, 112, 133, 0.4)'
                                            },
                                            '50%': {
                                                borderColor: 'rgba(102, 112, 133, 0.5)',
                                                boxShadow: '0 0 0 4px rgba(102, 112, 133, 0.1)'
                                            }
                                        }
                                    }, className: "client-Dropdown", children: _jsx(Dropdown, { value: selectedGroupAccountId || selectedClientId, options: clientOptions, onChange: handleClientChangeWithContext, placeholder: isArchiveMode ? "🔍 Select Archived Client" : "Select Client", searchable: true, disabled: loading || utilityLoading || refreshLoading, className: isArchiveMode && !selectedClientId ? 'archive-mode-empty' : '' }) }), activeTab === 'account_overview' && uniqueNetworkIcons.length > 0 && (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', ml: 0.5 }, children: uniqueNetworkIcons.map((group, idx) => (_jsx(HoverTooltip, { placement: "bottom", arrow: true, title: _jsxs(Box, { sx: { p: 0.5 }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: '12px', mb: 0.5 }, children: group.name }), group.accounts.map((acc, i) => (_jsxs(Typography, { sx: { fontSize: '11px', opacity: 0.9 }, children: ["\u2022 ", acc.accountName || 'Connected Account'] }, i)))] }), children: _jsx(Box, { sx: {
                                                width: 38,
                                                height: 38,
                                                borderRadius: '50%',
                                                bgcolor: '#F9FAFB',
                                                border: '2px solid #FFFFFF',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginLeft: idx === 0 ? 0 : '-16px',
                                                zIndex: idx,
                                                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    zIndex: 10,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.1), 0px 10px 15px -3px rgba(16, 24, 40, 0.1)'
                                                }
                                            }, children: _jsx(Box, { component: "img", src: group.icon, alt: group.name, sx: { width: 24, height: 24, objectFit: 'contain' } }) }) }, idx))) }))] }), rightSection: activeTab === 'account_overview' ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [groupAccountOptions.length > 1 && (_jsx(Dropdown, { value: selectedGroupAccountId || selectedClientId, options: groupAccountOptions.filter((option) => option.value === "" || option.value !== (selectedGroupAccountId || selectedClientId)), onChange: handleGroupChange, placeholder: "Select Group Store", disabled: loading || utilityLoading || refreshLoading, displayValue: _jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("img", { src: "/assets/users.svg", alt: "" }), _jsx("span", { style: { fontWeight: 500, color: '#000' }, children: "Group Store" })] }) })), clientType === 'Custom Client' && (_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        border: '1px solid #ddd',
                                        px: '12px',
                                        borderRadius: '8px',
                                        bgcolor: '#fff',
                                        height: '42px'
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, }, children: [_jsx("img", { src: "/assets/TypeC.svg", alt: "", style: { width: 16, height: 16 } }), _jsx(Typography, { sx: { fontWeight: 600, fontSize: '12px', color: '#101828' }, children: "Type:" }), _jsx(Typography, { sx: { fontSize: '12px', color: '#667085' }, children: "Custom Client" })] }), _jsx("img", { src: "/assets/target.svg", alt: "" }), _jsx(Switch, { checked: totalGoalToggle, onChange: (e) => {
                                                const val = e.target.checked;
                                                dispatch(setTotalGoalToggle(val));
                                                setContextGoalToggle(val);
                                            }, sx: {
                                                width: 44, height: 24, padding: 0,
                                                '& .MuiSwitch-switchBase': {
                                                    padding: '2px',
                                                    '&.Mui-checked': {
                                                        transform: 'translateX(20px)',
                                                        color: '#fff',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: '#ff008e',
                                                            opacity: 1
                                                        }
                                                    }
                                                },
                                                '& .MuiSwitch-thumb': { width: 20, height: 20 },
                                                '& .MuiSwitch-track': { borderRadius: 24 / 2, backgroundColor: '#E9E9EA', opacity: 1 }
                                            } }), _jsx(InfoTooltip, { title: _jsx(Box, { sx: { p: 1 }, children: _jsxs(Typography, { sx: { fontSize: '12px' }, children: ["When enabled, calculations will be based on the ", _jsx("b", { children: "Total Goal" }), " instead of the individual projection."] }) }), placement: "bottom" }), _jsx(Typography, { sx: { fontWeight: 500, fontSize: '12px', color: '#344054', whiteSpace: 'nowrap' }, children: "Total Goal" })] })), showActionDropdown && (_jsx(Dropdown, { value: effectiveAction, onChange: (val) => setSelectedAction(val), options: cjActionOptions, sortByLabel: false, disabled: loading || utilityLoading || refreshLoading, displayValue: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx("span", { style: { fontWeight: 500, color: '#101828' }, children: "Action:" }), _jsx("span", { style: { color: '#667085' }, children: selectedActionLabel })] }), size: "sm", className: "header-dropdown", fullWidth: false })), networkOptions.length > 1 && (_jsx(Dropdown, { value: selectedConnectionId, onChange: (val) => {
                                        setSelectedConnectionId(val);
                                        setSelectedAction(null); // Action belongs to the chosen connection - back to its global setting
                                    }, options: networkOptions, sortByLabel: false, disabled: loading || utilityLoading || refreshLoading, displayValue: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [selectedOption.value !== 'all' && NETWORK_METADATA_MAP[selectedOption.networkKey]?.icon && (_jsx("img", { src: NETWORK_METADATA_MAP[selectedOption.networkKey].icon, alt: "", style: { width: 16, height: 16, objectFit: 'contain' } })), _jsx("span", { style: { fontWeight: 500, color: '#101828' }, children: "Network:" }), _jsx("span", { style: { color: '#667085' }, children: truncateName(selectedOption.label, 20) })] }), size: "sm", className: "header-dropdown", fullWidth: false }))] })) : null }), activeTab === 'account_overview' && (_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, statusText: statusText, onRefresh: handleRefresh, disabled: statusText === "In process" || refreshLoading, loading: refreshLoading, moduleName: PARENT_MODULE_KEY, disabledReason: statusText === "In process" ? "Data is being updated in the background. Please wait." : "" }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(CustomDatePicker, { value: dateRange, compareValue: compareDateRange, onApply: handleDateChangeWithContext, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", disabled: loading }), _jsx(ExportButton, { onClick: handleExportOpen, loading: exporting, disabled: loading || visibleTabsWithRows.length === 0 })] }) })), _jsx(Box, { sx: {
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            bgcolor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB',
                            px: 1.5,
                            py: 1,
                        }, children: _jsx(CommonTabs, { selectedValue: activeTab, onChange: (val) => setActiveTab(val), tabs: visibleTabs }) }), loading ? (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: 'center' }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : !selectedId && isArchiveMode ? (
                    // ════ EMPTY STATE: Archive mode but no client selected ════
                    _jsxs(Box, { sx: {
                            p: '60px 16px',
                            borderRadius: '8px',
                            border: '1px solid #EAECF0',
                            bgcolor: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2.5
                        }, children: [_jsx(Box, { sx: {
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(102, 112, 133, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }, children: _jsxs("svg", { width: "40", height: "40", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z", stroke: "#667085", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M12 11V17", stroke: "#667085", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M9 14H15", stroke: "#667085", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M16 7V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V7", stroke: "#667085", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }), _jsxs(Box, { sx: { textAlign: 'center', maxWidth: '400px' }, children: [_jsx(Typography, { sx: {
                                            fontWeight: 600,
                                            fontSize: '18px',
                                            color: '#101828',
                                            mb: 1
                                        }, children: "Select an Archived Client" }), _jsx(Typography, { sx: {
                                            fontSize: '14px',
                                            color: '#667085',
                                            lineHeight: 1.6
                                        }, children: "Choose a client from the dropdown above to view their historical performance data and analytics." })] }), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1.5,
                                    borderRadius: '8px',
                                    bgcolor: 'rgba(102, 112, 133, 0.05)',
                                    border: '1px solid rgba(102, 112, 133, 0.1)'
                                }, children: [_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M8 5.33333V8", stroke: "#667085", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M8 10.6667H8.00667", stroke: "#667085", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z", stroke: "#667085", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })] }), _jsx(Typography, { sx: { fontSize: '13px', color: '#667085' }, children: "Archived clients are inactive but their data remains accessible" })] })] })) : (_jsxs(Box, { sx: { p: '16px', borderRadius: '8px', border: '1px solid #EAECF0', bgcolor: '#fff' }, children: [activeTab === 'account_overview' && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsx(MetricsGrid, { summary: summary, connections: allConnections, isCompareOn: isCompareOn, totalGoalToggle: totalGoalToggle, affiliateAccountType: affiliateAccountType }), visibleTabsWithRows.length > 0 && (_jsx(PublisherTable, { tabs: visibleTabsWithRows, activeTabKey: activeFunnelTab, onTabChange: handleFunnelTabChange, onPageChange: handlePageChange, onSortChange: handleSort, sortBy: tableState.sortBy, sortOrder: tableState.sortOrder, loading: loading, onRefresh: fetchData, hasCompare: isCompareOn }))] })), activeTab === 'publisher_tracker' && (_jsx(PublisherTracker, { clientName: selectedClient?.name || clientName || '' })), activeTab === 'social_tracker' && (_jsx(SocialYtTrackerList, { trackerType: "social", clientName: selectedClient?.name || clientName || '' })), activeTab === 'youtube_tracker' && (_jsx(SocialYtTrackerList, { trackerType: "youtube", clientName: selectedClient?.name || clientName || '' })), activeTab === 'sample_tracker' && (_jsx(SampleTrackerList, { clientName: selectedClient?.name || clientName || '' }))] })), _jsx(PublisherLimitModal, { open: isLimitModalOpen, onClose: () => setIsLimitModalOpen(false), onSubmit: handleExportSubmit, loading: exporting }), _jsx(Box, { sx: { position: 'absolute', left: '-9999px', top: '-9999px' }, children: _jsx("div", { ref: pdfRef, children: _jsx(AccountOverviewPDFContent, { summary: summary, tabsWithRows: exportTabs
                                    ? exportTabs.filter(t => !isClient || !CLIENT_HIDDEN_TABS.includes(t.key?.toLowerCase()))
                                    : visibleTabsWithRows, connections: connections, clientName: selectedClient?.name || clientName || 'Client', dateRange: dateRange, compareDateRange: isCompareOn ? compareDateRange : undefined, publisherLimit: publisherLimit }) }) })] })) }) }));
};
export default AccountOverview;
